import { z } from 'zod';
import { identifiableValidationSchema } from './identifiable-validation-schema.js';
import { StudyEntityType } from '../study-entity-type.js';
import { uuidish } from '../../utils/uuidish.js';
import { nullishToUndefined } from '../../utils/nullishToUndefined.js';
/**
 * Validation rules for a `StudyEntity`.  This validation schema can be used in conjunction with other `zod`
 * validation schemas to validate entities using a combination of `zod`'s `z.object` and the `.shape` of
 * each validation schema:
 *
 * @example
 * export function manySchemasCombined = z.object({
 *   ...baseStudyEntityValidationSchema.shape,
 *   ...sortableStudyEntityValidationSchema.shape,
 *   ...paginationValidationSchema.shape
 * });
 *
 * Alternately, `zod`'s `intersection()` method can be used to "and" two (or more) validation schemas together:
 *
 * @example
 * export function someStudyEntityValidationSchema = z.intersection(
 *   baseStudyEntityValidationSchema,
 *   z.object({
 *       activityId: uuidish.describe('activityId is required'),
 *       sequenceNumber: z.number().describe('sequenceNumber is required')
 *   })
 * );
 *
 * @see https://zod.dev/README?id=shape
 * @see https://zod.dev/?id=intersections
 */
export const baseStudyEntityValidationSchema = z.object({
    ...identifiableValidationSchema.shape,
    studyId: uuidish.describe('studyId is required'),
    entityType: z.enum(StudyEntityType),
    deleted: z.coerce.boolean(),
    // Defaulting to `now` is temporary until we break out separate 'create'/operation models
    created: z.coerce.date().default(new Date()),
    // Defaulting to `now` is temporary until we break out separate 'create'/operation models
    updated: z.coerce.date().default(new Date()),
    createdBy: uuidish.nullish().transform(nullishToUndefined).optional(),
    revision: z.number().nullish().transform(nullishToUndefined).optional(),
});
//# sourceMappingURL=base-study-entity-validation-schema.js.map